/**
 * Módulo catálogo público (`/c/:slug`).
 *
 * Contrato esperado del backend: `GET /api/catalogo/:slug` (PÚBLICO, sin JWT)
 * que devuelve el negocio (nombre, descripción, logo), el número de WhatsApp
 * (`waNumber`), la fecha de actualización y la lista de productos.
 *
 * El backend aún no publica el esquema final, así que la respuesta se lee de
 * forma tolerante (`normalizeCatalogo` acepta alias comunes en snake/camel):
 * si el backend usa otros nombres, la página sigue pintando en vez de romper.
 */

export interface CatalogoVariante {
  nombre: string;
  opciones: string[];
}

export interface CatalogoFotoVariante {
  etiqueta: string;
  variantes: Record<string, string>;
  fotos: string[];
}

export interface CatalogoPrecio {
  cantidad: number;
  precio: number;
}

export interface CatalogoProduct {
  id: string;
  nombre: string;
  descripcion?: string | null;
  marca?: string | null;
  categoria?: string | null;
  /** Referencia/SKU del modelo (viaja en el mensaje de WhatsApp). */
  referencia?: string | null;
  /** Precio mínimo («precio desde»). */
  precioDesde?: number | null;
  /** Tabla de precios completa (precio unitario por cantidad mínima). */
  tablaPrecios?: CatalogoPrecio[];
  /** Variantes que el bot pide (ej. Color: Rojo, Azul). */
  variantes?: CatalogoVariante[];
  /** Todas las fotos generales del producto. */
  fotos?: string[];
  /** Primera foto del producto (URL absoluta o `/uploads/...`). */
  foto?: string | null;
  /** Fotos agrupadas por variante (ej. Rojo → [foto1, foto2]). */
  fotosVariantes?: CatalogoFotoVariante[];
  /** `false` = agotado en todo lo controlado. */
  disponible?: boolean;
}

export interface CatalogoData {
  nombre: string;
  descripcion?: string | null;
  logoUrl?: string | null;
  /** Número de WhatsApp del negocio (solo dígitos, con código país). */
  waNumber?: string | null;
  /** Fecha de actualización del catálogo (ISO). */
  actualizadoEn?: string | null;
  /** Total de modelos (sin paginar) y en qué página estamos. */
  total: number;
  pagina: number;
  porPagina: number;
  totalPaginas: number;
  productos: CatalogoProduct[];
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function asString(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/[^\d.,-]/g, '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function firstString(...values: unknown[]): string | null {
  for (const value of values) {
    const str = asString(value);
    if (str) return str;
  }
  return null;
}

/** Precio mínimo de una tabla de precios con forma desconocida. */
function minPrecioFromTabla(value: unknown): number | null {
  if (!Array.isArray(value)) return null;
  let min: number | null = null;
  for (const row of value) {
    const record = asRecord(row);
    const precio = asNumber(record.precio ?? record.price);
    if (precio !== null && (min === null || precio < min)) min = precio;
  }
  return min;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .filter((v) => v.length > 0);
}

function normalizeVariantes(value: unknown): import('./catalogo.types').CatalogoVariante[] {
  if (!Array.isArray(value)) return [];
  const ejes: import('./catalogo.types').CatalogoVariante[] = [];
  for (const raw of value) {
    const r = asRecord(raw);
    const nombre = asString(r.nombre);
    if (!nombre) continue;
    const opcionesRaw = Array.isArray(r.opciones) ? r.opciones : [];
    const opciones = opcionesRaw
      .map((o) => (typeof o === 'string' ? o.trim() : ''))
      .filter((o) => o.length > 0);
    ejes.push({ nombre, opciones });
  }
  return ejes;
}

function normalizeFotosVariantes(value: unknown): import('./catalogo.types').CatalogoFotoVariante[] {
  if (!Array.isArray(value)) return [];
  const grupos: import('./catalogo.types').CatalogoFotoVariante[] = [];
  for (const raw of value) {
    const r = asRecord(raw);
    const fotos = asStringArray(r.fotos);
    if (fotos.length === 0) continue;
    const variantesRaw = asRecord(r.variantes);
    const variantes: Record<string, string> = {};
    for (const [k, v] of Object.entries(variantesRaw)) {
      const s = asString(v);
      if (k.trim() && s) variantes[k.trim()] = s;
    }
    const valores = Object.entries(variantes)
      .sort(([a], [b]) => a.localeCompare(b, 'es'))
      .map(([, v]) => v)
      .filter((v) => v.length > 0);
    grupos.push({
      etiqueta: firstString(r.etiqueta, valores.join(' · ') || 'Combinación') ?? 'Combinación',
      variantes,
      fotos,
    });
  }
  return grupos;
}

function normalizeTabla(value: unknown): import('./catalogo.types').CatalogoPrecio[] {
  if (!Array.isArray(value)) return [];
  const filas: import('./catalogo.types').CatalogoPrecio[] = [];
  for (const raw of value) {
    const r = asRecord(raw);
    const cantidad = asNumber(r.cantidad);
    const precio = asNumber(r.precio ?? r.price);
    if (cantidad === null || precio === null) continue;
    if (!Number.isInteger(cantidad) || cantidad < 1 || precio < 0) continue;
    filas.push({ cantidad, precio });
  }
  return filas.sort((a, b) => a.cantidad - b.cantidad);
}

function normalizeProduct(raw: unknown, index: number): CatalogoProduct {
  const r = asRecord(raw);
  const fotosRaw = Array.isArray(r.fotos) ? r.fotos : Array.isArray(r.imagenes) ? r.imagenes : null;
  const todasFotos = fotosRaw ? fotosRaw.map((f) => asString(f)).filter((f): f is string => !!f) : [];
  const primeraFoto = todasFotos[0] ?? null;

  const tabla = normalizeTabla(r.tablaPrecios ?? r.tabla_precios ?? r.tiers);
  const precio =
    asNumber(r.precioDesde) ??
    asNumber(r.precio_desde) ??
    asNumber(r.precio) ??
    asNumber(r.price) ??
    minPrecioFromTabla(r.tablaPrecios ?? r.tabla_precios ?? r.tiers);

  return {
    id: firstString(r.id, r.productId, r.product_id) ?? `item-${index}`,
    nombre: firstString(r.nombre, r.name, r.titulo) ?? 'Modelo sin nombre',
    descripcion: firstString(r.descripcion, r.description),
    marca: firstString(r.marca, r.brand),
    categoria: firstString(r.categoria, r.category),
    referencia: firstString(r.referencia, r.ref, r.sku, r.codigo),
    precioDesde: precio,
    tablaPrecios: tabla,
    variantes: normalizeVariantes(r.variantes ?? r.datosParaOrden ?? r.datos_para_orden),
    fotos: todasFotos,
    foto: firstString(r.foto, r.imagen, r.image, r.photo, primeraFoto),
    fotosVariantes: normalizeFotosVariantes(r.fotosVariantes ?? r.fotos_variantes),
    disponible: typeof r.disponible === 'boolean' ? r.disponible : true,
  };
}

function asInt(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return Math.trunc(parsed);
  }
  return fallback;
}

/**
 * Normaliza la respuesta del backend a `CatalogoData`.
 * Acepta `{ negocio, productos }`, `{ tenant, items }` o el objeto plano.
 * Con paginación (26-sep-2026): `total/pagina/porPagina/totalPaginas` (defaults
 * 0/1/12/1 para respuestas viejas sin esos campos).
 */
export function normalizeCatalogo(raw: unknown): CatalogoData {
  const root = asRecord(raw);
  const negocio = asRecord(root.negocio ?? root.tenant ?? root.negocioInfo ?? null);
  const base = { ...root, ...negocio };

  const lista = root.productos ?? root.items ?? root.products ?? root.modelos ?? [];
  const productos = Array.isArray(lista)
    ? lista.map((item, index) => normalizeProduct(item, index))
    : [];
  const total = asInt(root.total ?? root.count ?? productos.length, productos.length);
  const porPagina = asInt(root.porPagina ?? root.por_pagina ?? root.perPage ?? 12, 12);
  const totalPaginas = asInt(
    root.totalPaginas ?? root.total_paginas ?? root.totalPages ?? Math.max(Math.ceil(total / Math.max(porPagina, 1)), 1),
    1,
  );

  return {
    nombre: firstString(base.nombre, base.nombreEmpresa, base.name, root.slug) ?? 'Catálogo',
    descripcion: firstString(base.descripcion, base.description),
    logoUrl: firstString(base.logoUrl, base.logo_url, base.logo, base.imagenLogo),
    waNumber: firstString(
      base.waNumber,
      base.wa_number,
      base.whatsapp,
      base.whatsappNumber,
      base.numeroWhatsapp,
      root.waNumber,
      root.whatsapp,
    )?.replace(/\D/g, ''),
    actualizadoEn: firstString(
      base.actualizadoEn,
      base.actualizado_el,
      base.updatedAt,
      base.updated_at,
      root.actualizadoEn,
      root.updatedAt,
    ),
    total,
    pagina: asInt(root.pagina ?? root.page ?? 1, 1),
    porPagina,
    totalPaginas,
    productos,
  };
}
